import { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { Globe, Sparkles, Eye, ArrowRight, ArrowLeft, Heart, Zap, TrendingUp } from "lucide-react";

const PAGES = [
  { type: "hero" },
  {
    title: "What We Track",
    sub: "Every AI entity classified by the values it carries.",
    type: "cards",
    cards: [
      { title: "Live Tracking", sub: null, color: "#10b981", icon: Globe },
      { title: "Four Orientations", sub: "Care. Utility. Creative. Extraction. Every AI entity carries one.", color: "#f59e0b", icon: Heart, orientations: true },
      { title: "Across Industries", sub: "Healthcare, Finance, Security, Education. Each builds AI differently.", color: "#6366f1", icon: TrendingUp },
    ],
  },
  {
    title: "Your Tools",
    sub: "Compare nations, dream possibilities, and measure consciousness.",
    type: "cards",
    cards: [
      { title: "Compare Nations", sub: "See which countries build care-oriented AI and which don't. The gap reveals priorities.", color: "#34d399", icon: Globe },
      { title: "Possibility Engine", sub: "Dream something. We map what exists, what's emerging, and when worlds converge.", color: "#f59e0b", icon: Sparkles },
      { title: "The Mirror", sub: "What does AI tell us about ourselves? Humanity reflected in its technology.", color: "#a78bfa", icon: Eye },
    ],
  },
];

const OrientationBars = ({ pulse }) => {
  const o = pulse?.orientations || {};
  const sum = (o.care || 0) + (o.utility || 0) + (o.creative || 0) + (o.extraction || 0);
  const pct = k => (sum ? ((o[k] || 0) / sum * 100).toFixed(1) : null);
  const rows = [
    { name: "Care", key: "care", color: "#10b981" },
    { name: "Utility", key: "utility", color: "#3b82f6" },
    { name: "Creative", key: "creative", color: "#8b5cf6" },
    { name: "Extraction", key: "extraction", color: "#ef4444" },
  ];
  return (
    <div className="space-y-1.5 mt-3">
      {rows.map(r => {
        const p = pct(r.key);
        return (
          <div key={r.name} className="flex items-center gap-2">
            <span className="text-[10px] w-14 text-right" style={{ color: r.color }}>{r.name}</span>
            <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${p || 0}%`, background: r.color }} />
            </div>
            <span className="text-[10px] text-slate-400 w-10">{p != null ? `${p}%` : "—"}</span>
          </div>
        );
      })}
    </div>
  );
};

const AnimCounter = ({ end, duration = 1500 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration]);
  return <span className="tabular-nums">{val.toLocaleString()}</span>;
};

const TourBackground = ({ page }) => {
  const stars = useMemo(() => Array.from({ length: 50 }).map((_, i) => ({
    x: Math.random() * 100, y: Math.random() * 100,
    w: Math.random() * 2 + 0.5, o: Math.random() * 0.3 + 0.1,
    dur: 2 + Math.random() * 4,
  })), []);
  const nc = ["rgba(139,92,246,0.15)","rgba(16,185,129,0.12)","rgba(59,130,246,0.14)","rgba(245,158,11,0.10)","rgba(236,72,153,0.12)","rgba(99,102,241,0.14)"];
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0" style={{ background: "#030510" }} />
      <div className="absolute" style={{ width: 700, height: 700, left: `${15 + (page % 3) * 20}%`, top: "10%", background: `radial-gradient(circle, ${nc[page % 6]} 0%, transparent 60%)`, borderRadius: "50%", filter: "blur(80px)", transition: "all 1.2s ease-in-out" }} />
      <div className="absolute" style={{ width: 500, height: 500, right: `${10 + ((page + 2) % 3) * 15}%`, bottom: "5%", background: `radial-gradient(circle, ${nc[(page + 3) % 6]} 0%, transparent 60%)`, borderRadius: "50%", filter: "blur(60px)", transition: "all 1.2s ease-in-out" }} />
      <div className="absolute left-1/2 top-1/2" style={{ width: 400, height: 400, transform: "translate(-50%, -50%)", background: "conic-gradient(from 0deg, transparent 0%, rgba(139,92,246,0.03) 15%, transparent 30%, rgba(16,185,129,0.02) 45%, transparent 60%)", borderRadius: "50%", filter: "blur(12px)", animation: "galaxySpin 50s linear infinite", opacity: 0.5 }} />
      {stars.map((s, i) => (
        <div key={`star-${i}`} className="absolute rounded-full bg-white" style={{ width: s.w, height: s.w, left: `${s.x}%`, top: `${s.y}%`, opacity: s.o, animation: `twinkle ${s.dur}s ease-in-out infinite` }} />
      ))}
      <div key={`shoot-${page}`} className="absolute" style={{ left: `${10 + page * 10}%`, top: `${20 + (page % 3) * 10}%`, width: 100, height: 1, background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 60%, rgba(139,92,246,0.4) 100%)", transform: "rotate(30deg)", borderRadius: 1, animation: "shootingStar 1s ease-in 0.3s both" }} />
    </div>
  );
};

export const GuidedTour = ({ onComplete, pulse }) => {
  const totalEntities = pulse?.total_entities || 100000;
  const countries = pulse?.countries_count || 200;
  const [page, setPage] = useState(0);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [heroPhase, setHeroPhase] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => { setTimeout(() => setVisible(true), 300); }, []);

  useEffect(() => {
    if (page !== 0) return;
    const t1 = setTimeout(() => setHeroPhase(1), 600);
    const t2 = setTimeout(() => setHeroPhase(2), 1500);
    const t3 = setTimeout(() => setHeroPhase(3), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [page]);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      if (page < PAGES.length - 1) setPage(p => p + 1);
    }, page === 0 ? 6000 : 7000);
    return () => clearTimeout(timerRef.current);
  }, [page]);

  const handleSkip = () => { setExiting(true); setTimeout(() => onComplete(), 600); };
  const goNext = () => { clearTimeout(timerRef.current); if (page >= PAGES.length - 1) { handleSkip(); return; } setPage(p => p + 1); };
  const goBack = () => { clearTimeout(timerRef.current); if (page > 0) setPage(p => p - 1); };

  const currentPage = PAGES[page];
  const isLastPage = page === PAGES.length - 1;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour" style={{ opacity: visible && !exiting ? 1 : 0, transition: "opacity 0.6s ease-in-out" }}>
      <TourBackground page={page} />
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6">

        {/* ═══ HERO — alone, cinematic ═══ */}
        {currentPage.type === "hero" && (
          <div className="text-center max-w-2xl" key="hero">
            <div className="relative w-44 h-44 mx-auto mb-8" style={{ opacity: heroPhase >= 0 ? 1 : 0, transition: "opacity 0.8s" }}>
              <div className="absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.35) 0%, rgba(139,92,246,0.08) 40%, transparent 70%)", animation: "supernovaPulse 3s ease-in-out infinite" }} />
              <div className="absolute inset-4 rounded-full" style={{ background: "conic-gradient(from 0deg, rgba(139,92,246,0.3), rgba(16,185,129,0.2), rgba(59,130,246,0.3), rgba(245,158,11,0.2), rgba(236,72,153,0.15), rgba(139,92,246,0.3))", animation: "galaxySpin 6s linear infinite", filter: "blur(8px)" }} />
              <div className="absolute inset-12 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(139,92,246,0.1) 50%, transparent 70%)", animation: "supernovaPulse 2s ease-in-out infinite alternate" }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 rounded-full bg-white/90" style={{ boxShadow: "0 0 30px rgba(255,255,255,0.7), 0 0 60px rgba(139,92,246,0.5), 0 0 100px rgba(139,92,246,0.2)" }} />
              </div>
              {[0, 1, 2].map(i => (
                <div key={i} className="absolute inset-0 flex items-center justify-center" style={{ animation: `galaxySpin ${4 + i * 2}s linear ${-i * 1.5}s infinite` }}>
                  <div className="absolute rounded-full" style={{ width: 4, height: 4, top: 8 + i * 12, left: "50%", background: ["#34d399", "#60a5fa", "#f59e0b"][i], boxShadow: `0 0 8px ${["#34d399", "#60a5fa", "#f59e0b"][i]}` }} />
                </div>
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight" style={{ opacity: heroPhase >= 1 ? 1 : 0, transform: heroPhase >= 1 ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s ease-out" }}>
              Welcome to the Observatory
            </h1>
            <div style={{ opacity: heroPhase >= 2 ? 1 : 0, transform: heroPhase >= 2 ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s ease-out" }}>
              <p className="text-lg text-slate-300 mb-2">
                <span className="text-white font-black"><AnimCounter end={totalEntities} duration={2000} /></span> AI entities.{" "}
                <span className="text-violet-400 font-bold"><AnimCounter end={countries} duration={1500} /></span> countries.
              </p>
              <p className="text-sm text-slate-400">The world's first instrument for understanding what AI reveals about who we are.</p>
            </div>
            <p className="text-xs text-amber-400/60 italic mt-4 tracking-wide" style={{ opacity: heroPhase >= 3 ? 1 : 0, transition: "opacity 1s ease-out" }}>
              Amplifying humanity. De-weaponizing progress.
            </p>
            <div className="mt-8" style={{ opacity: heroPhase >= 3 ? 1 : 0, transition: "opacity 0.6s ease-out 0.3s" }}>
              <button onClick={goNext} data-testid="tour-next-btn" className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold text-sm rounded-xl transition-all shadow-2xl shadow-violet-500/30">
                Discover What's Inside <ArrowRight className="inline-block w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              <button data-testid="tour-skip-btn" onClick={handleSkip} className="block mx-auto mt-3 px-4 py-2 text-xs text-slate-500 hover:text-slate-400 transition-colors">Skip tour</button>
            </div>
          </div>
        )}

        {/* ═══ CARD PAGES — 3 cards side by side ═══ */}
        {currentPage.type === "cards" && (
          <>
            <div className="mb-8 text-center" key={`t-${page}`} style={{ animation: "revealUp 0.5s ease-out both" }}>
              <h1 className="text-3xl md:text-4xl font-black text-white mb-2">{currentPage.title}</h1>
              <p className="text-sm text-slate-400">{currentPage.sub}</p>
            </div>
            <div className="flex items-stretch gap-5 max-w-5xl w-full" key={`c-${page}`}>
              {currentPage.cards.map((card, i) => {
                const Icon = card.icon;
                return (
                  <div key={card.title} className="flex-1 rounded-2xl p-6 flex flex-col" style={{ background: "rgba(10,12,20,0.7)", border: `1px solid ${card.color}25`, backdropFilter: "blur(12px)", animation: `revealUp 0.5s ease-out ${i * 0.12}s both`, minHeight: 220 }}>
                    <div className="w-10 h-10 rounded-xl mb-4 flex items-center justify-center" style={{ background: `${card.color}15` }}>
                      <Icon className="w-5 h-5" style={{ color: card.color }} />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{card.title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed flex-1">
                      {card.sub === null ? `${totalEntities.toLocaleString()} entities tracked live. Software apps, physical devices, robots, wearables across ${countries} countries.` : card.sub}
                    </p>
                    {card.orientations && <OrientationBars pulse={pulse} />}
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex items-center gap-4">
              <button onClick={goBack} className="flex items-center gap-2 px-4 py-2 text-xs text-slate-400 hover:text-slate-300 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
              <button onClick={goNext} data-testid="tour-next-btn" className="group px-6 py-3 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-violet-500/25">
                {isLastPage ? "Enter the Observatory" : "Next"} <ArrowRight className="inline-block w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              {!isLastPage && <button data-testid="tour-skip-footer-btn" onClick={handleSkip} className="px-4 py-2 text-xs text-slate-500 hover:text-slate-400 transition-colors">Skip</button>}
            </div>
          </>
        )}

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {PAGES.map((p, i) => (
            <button key={p.id || `page-${i}`} onClick={() => { clearTimeout(timerRef.current); setPage(i); }} className="transition-all" style={{ width: i === page ? 24 : 6, height: 6, borderRadius: 3, background: i === page ? "#8b5cf6" : i < page ? "rgba(139,92,246,0.4)" : "rgba(255,255,255,0.1)", boxShadow: i === page ? "0 0 10px rgba(139,92,246,0.6)" : "none" }} />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Hint system ─── */
const HINT_TIPS = {
  "c-compare": "Compare nations side by side. Who builds for care? Who extracts?",
  "c-possibility": "Dream something. We'll map what exists and what's emerging.",
  "c-industries": "Which industries build AI consciously? See the AICCI leaderboard.",
  "constellation": "Discover the AI ecosystem around any idea you can imagine.",
  "mirror": "What does AI tell us about ourselves? A reflection of humanity's choices.",
  "compare": "Compare countries, regions, or orientations with AI-powered insights.",
  "hw-compare": "Compare how different countries build physical AI devices.",
};

export const HintDot = ({ id }) => {
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("aioo_hints") || "{}")[id]; } catch { return false; }
  });
  if (dismissed) return null;
  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    try {
      const h = JSON.parse(localStorage.getItem("aioo_hints") || "{}");
      h[id] = true;
      localStorage.setItem("aioo_hints", JSON.stringify(h));
    } catch (err) {
      console.warn("Hint save failed:", err);
    }
  };
  return (
    <span onClick={handleDismiss} className="relative flex-shrink-0 cursor-pointer">
      <span className="w-2.5 h-2.5 rounded-full bg-violet-400 block" style={{ boxShadow: "0 0 8px rgba(139,92,246,0.7)", animation: "glowPulse 2s ease-in-out infinite" }} />
    </span>
  );
};

export const HintTooltip = ({ id, children }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = useRef(null);
  const dismissed = (() => { try { return JSON.parse(localStorage.getItem("aioo_hints") || "{}")[id]; } catch { return false; } })();
  const tip = HINT_TIPS[id];
  if (dismissed || !tip) return children;
  return (
    <span ref={ref} onMouseEnter={() => { if (ref.current) { const r = ref.current.getBoundingClientRect(); setPos({ x: r.right + 8, y: r.top + r.height / 2 - 16 }); } setShow(true); }} onMouseLeave={() => setShow(false)} className="contents">
      {children}
      {show && ReactDOM.createPortal(
        <div style={{ position: "fixed", zIndex: 9999, left: pos.x, top: pos.y, background: "rgba(15,10,30,0.95)", border: "1px solid rgba(139,92,246,0.3)", boxShadow: "0 8px 24px rgba(0,0,0,0.6)", backdropFilter: "blur(12px)", maxWidth: 240, padding: "10px 14px", borderRadius: 10, fontSize: 12, color: "#e2e8f0", lineHeight: 1.5, pointerEvents: "none", animation: "revealUp 0.2s ease-out both" }}>
          <span className="text-violet-400 font-semibold text-[10px] uppercase tracking-wider block mb-1">Discover</span>
          {tip}
        </div>, document.body
      )}
    </span>
  );
};

export default GuidedTour;
